import React, { useEffect, useMemo, useState } from 'react';
import { useArtists, useFestivalDays } from '../lib/usePublicData';
import ArtistModal from './ArtistModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ACCENT_RING = {
    pink: 'zz-ring-pink',
    mint: 'zz-ring-mint',
    yellow: 'zz-ring-yellow',
    lilac: 'zz-ring-lilac',
};
const ACCENT_BG = {
    pink: 'var(--pink-soft)',
    mint: 'var(--mint-soft)',
    yellow: 'var(--yellow-soft)',
    lilac: 'var(--lilac)',
};

const ArtistCard = ({ artist, large = false, onOpen }) => {
    const accent = artist.accent || 'mint';
    const ring = ACCENT_RING[accent] || 'zz-ring-mint';
    const initials = artist.name?.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';

    let photoSrc = artist.photo_url || artist.photo || '';
    // /api/ paths blijven relatief: browser resolved tegen huidige origin
    // (zottezomerronse.be op prod, preview host op dev) — deploy-safe.

    const onKey = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(artist);
        }
    };

    return (
        <article
            data-testid={`artist-card-${artist.slug || artist.id}`}
            className="zz-artist-card zz-artist-card-clickable flex flex-col items-center text-center group w-full max-w-[280px]"
            role="button"
            tabIndex={0}
            aria-haspopup="dialog"
            aria-label={`Toon meer info over ${artist.name}`}
            onClick={() => onOpen(artist)}
            onKeyDown={onKey}
        >
            <div className={`zz-artist-circle ${ring} ${large ? 'w-44 sm:w-56' : 'w-32 sm:w-40'} mx-auto`}>
                {photoSrc ? (
                    <img src={photoSrc} alt={`Portretfoto ${artist.name}`} loading="lazy" decoding="async" width="800" height="800"
                        style={{ objectPosition: artist.ring_object_position || artist.ringObjectPosition || 'center' }} />
                ) : (
                    <div
                        className="w-full h-full flex items-center justify-center font-display text-4xl sm:text-5xl text-[var(--green-dark)]"
                        style={{ backgroundColor: ACCENT_BG[accent] || ACCENT_BG.mint }}>
                        {initials}
                    </div>
                )}
            </div>
            <h3 className={`mt-4 font-display zz-outline-title-dark ${large ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'}`}>
                {artist.name}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-[#006b44]/80">
                {artist.day || (artist._displayDate || '')} {artist.location && `· ${artist.location}`}
            </p>
        </article>
    );
};

export const Artists = () => {
    const [activeDay, setActiveDay] = useState('all');
    const [openArtist, setOpenArtist] = useState(null);
    const { data: artistsRaw } = useArtists();
    const { data: daysRaw } = useFestivalDays();

    // Merge dayMeta to derive accent, display_date for fallback artists
    const dayMap = useMemo(() => {
        const m = {};
        daysRaw.forEach((d) => { m[d.id] = d; });
        return m;
    }, [daysRaw]);

    const enriched = useMemo(() => artistsRaw.map((a) => {
        const dayId = a.festival_day_id || a.dayId;
        const day = dayMap[dayId];
        return {
            ...a,
            dayId,
            accent: a.accent || (day?.accent_color || day?.accent || 'mint'),
            day: a.day || (day?.display_date || day?.date || ''),
            location: a.location || day?.location || '',
        };
    }), [artistsRaw, dayMap]);

    useEffect(() => {
        const sync = () => {
            const hash = window.location.hash || '';
            const m = hash.match(/[?&]day=([^&]+)/);
            if (m) {
                const d = decodeURIComponent(m[1]);
                if (d === 'all' || daysRaw.some((x) => x.id === d)) {
                    setActiveDay(d);
                }
            }
        };
        sync();
        window.addEventListener('hashchange', sync);
        return () => window.removeEventListener('hashchange', sync);
    }, [daysRaw]);

    const featured = enriched
        .filter((a) => a.featured && (activeDay === 'all' || a.dayId === activeDay))
        .sort((a, b) => (a.sort_order ?? a.sortOrder ?? 0) - (b.sort_order ?? b.sortOrder ?? 0));
    const others = enriched
        .filter((a) => !a.featured && (activeDay === 'all' || a.dayId === activeDay))
        .sort((a, b) => (a.sort_order ?? a.sortOrder ?? 0) - (b.sort_order ?? b.sortOrder ?? 0));

    const tabs = [
        { id: 'all', label: 'Alle dagen' },
        ...daysRaw.map((d) => ({ id: d.id, label: d.display_date || d.date })),
    ];

    return (
        <section id="artiesten" data-testid="artists-section" className="relative py-16 sm:py-24 bg-vakjes">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="bg-cream/95 backdrop-blur-sm border-2 border-[var(--green)] rounded-[32px] p-6 sm:p-12 shadow-[0_6px_0_var(--green-dark)]">
                    <div className="text-center mb-8 sm:mb-10">
                        <h2 data-testid="artists-title" className="zz-outline-title font-display"
                            style={{ fontSize: 'clamp(2.25rem, 7vw, 4.5rem)' }}>
                            ARTIESTEN
                        </h2>
                        <p className="mt-4 text-sm sm:text-base text-[#006b44]/80">Filter per festivaldag.</p>
                    </div>

                    <div role="tablist" aria-label="Filter artiesten op dag" data-testid="artists-filter-tabs"
                        className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10 sm:mb-12">
                        {tabs.map((d) => {
                            const active = activeDay === d.id;
                            // Inline-style is intentioneel: vermijdt Tailwind Preflight (button { background:transparent })
                            // die in productie build over arbitrary value classes wint. Inline style heeft hoogste cascade.
                            const baseStyle = {
                                fontFamily: "'Bungee', system-ui, sans-serif",
                                fontWeight: 400,
                                whiteSpace: 'nowrap',
                                lineHeight: 1,
                                fontSize: 'clamp(0.75rem, 1.4vw, 0.875rem)',
                                padding: '10px 20px',
                                minHeight: '44px',
                                borderRadius: '999px',
                                borderWidth: '2px',
                                borderStyle: 'solid',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'visible',
                                transition: 'transform 200ms, box-shadow 200ms, background-color 200ms',
                                WebkitTextStroke: '0',
                                textShadow: 'none',
                                cursor: 'pointer',
                            };
                            const activeStyle = {
                                ...baseStyle,
                                backgroundColor: '#008354',
                                borderColor: '#006b44',
                                color: '#ffffff',
                                boxShadow: '0 3px 0 #006b44',
                            };
                            const inactiveStyle = {
                                ...baseStyle,
                                backgroundColor: '#f6f4f0',
                                borderColor: '#008354',
                                color: '#006b44',
                            };
                            return (
                                <button key={d.id} type="button" role="tab" aria-selected={active}
                                    data-testid={`artists-tab-${d.id}`}
                                    onClick={() => setActiveDay(d.id)}
                                    style={active ? activeStyle : inactiveStyle}>
                                    {d.label}
                                </button>
                            );
                        })}
                    </div>

                    {featured.length > 0 && (
                        <div data-testid="artists-featured-grid"
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 mb-12 place-items-center">
                            {featured.map((a) => <ArtistCard key={a.id} artist={a} large onOpen={setOpenArtist} />)}
                        </div>
                    )}

                    {others.length > 0 && (
                        <div data-testid="artists-others-grid"
                            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-7 sm:gap-9 place-items-center">
                            {others.map((a) => <ArtistCard key={a.id} artist={a} onOpen={setOpenArtist} />)}
                        </div>
                    )}

                    {featured.length === 0 && others.length === 0 && (
                        <p className="text-center text-[#006b44]/70 py-12">
                            Nog geen artiesten voor deze selectie.
                        </p>
                    )}
                </div>
            </div>

            <ArtistModal artist={openArtist} onClose={() => setOpenArtist(null)} />
        </section>
    );
};

export default Artists;
